import React, { useState } from 'react';
import { Search, Shield, CheckCircle, AlertTriangle, Target, FileText, Clock, ChevronDown, ChevronUp, Award, TrendingUp } from 'lucide-react';
import RegistrationModal from '../../components/RegistrationModal';
import SEO from '../../components/SEO';
import ServicePricing from '../../components/ServicePricing';
import CTASection from '../../components/CTASection';

const TrademarkClearancePage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalMode, setModalMode] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openModal = (mode) => {
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setModalMode(null);
  };

  const benefits = [
    {
      icon: Shield,
      title: "Risk Mitigation",
      description: "Identify potential conflicts before investing in brand development, marketing, and USPTO filing fees."
    },
    {
      icon: Search,
      title: "Comprehensive Search",
      description: "Search federal registrations, state records, common law uses, domain names, and social media handles."
    },
    {
      icon: Award,
      title: "Expert Analysis",
      description: "Attorney opinion on registration likelihood and infringement risks with strategic recommendations."
    },
    {
      icon: Target,
      title: "Strategic Alternatives",
      description: "If conflicts exist, we suggest modifications or alternative marks to ensure successful registration."
    },
    {
      icon: FileText,
      title: "Detailed Report",
      description: "Professional clearance report documenting search results, risk assessment, and recommendations."
    },
    {
      icon: TrendingUp,
      title: "Investment Protection",
      description: "Avoid costly rebranding, legal disputes, and wasted marketing spend on unavailable marks."
    }
  ];

  const searchLevels = [
    {
      title: "Federal Database Search",
      description: "USPTO TESS database for registered and pending trademarks",
      coverage: "All federal trademark registrations and applications"
    },
    {
      title: "State Registrations",
      description: "Secretary of State databases for state-level trademark registrations",
      coverage: "50 state trademark databases"
    },
    {
      title: "Common Law Search",
      description: "Business directories, domain names, social media, and web presence",
      coverage: "Unregistered but active trademark uses"
    },
    {
      title: "Industry Analysis",
      description: "Competitor trademarks and industry-specific trademark patterns",
      coverage: "Relevant competitive landscape assessment"
    }
  ];

  const process = [
    {
      step: "1",
      title: "Mark Submission",
      description: "Submit your proposed trademark for comprehensive analysis. We gather all details needed for an thorough search.",
      timeline: "Day 1"
    },
    {
      step: "2",
      title: "Multi-Level Search",
      description: "Our team conducts a thorough search across federal, state, and common law databases using advanced AI tools.",
      timeline: "Days 2-4"
    },
    {
      step: "3",
      title: "Analysis & Recommendations",
      description: "Receive a detailed risk assessment report with expert legal opinions and actionable recommendations for your mark.",
      timeline: "Days 5-7"
    }
  ];

  const packages = [
    {
      name: "Basic Clearance",
      price: "$299",
      description: "Federal database search",
      features: [
        "USPTO TESS database search",
        "Identical and similar marks",
        "Relevant trademark classes",
        "Basic conflict analysis",
        "Search results report",
        "Email delivery"
      ],
      popular: false
    },
    {
      name: "Comprehensive Clearance",
      price: "$599",
      description: "Full clearance search",
      features: [
        "Everything in Basic",
        "State trademark databases",
        "Common law search",
        "Domain name availability",
        "Attorney risk analysis",
        "Detailed written opinion",
        "Strategic recommendations"
      ],
      popular: true
    },
    {
      name: "Premium Clearance",
      price: "$999",
      description: "Complete due diligence",
      features: [
        "Everything in Comprehensive",
        "International trademark search",
        "Social media handle check",
        "Industry competitor analysis",
        "Multiple mark variations",
        "Priority processing",
        "Phone consultation"
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: "What is a trademark clearance search and why is it important?",
      answer: "A trademark clearance search investigates whether your proposed trademark is available for use and registration. It identifies existing identical or similar marks that could block your registration or expose you to infringement claims. Clearance is crucial because: (1) USPTO filing fees ($250-$350 per class) are non-refundable even if rejected, (2) rebranding after launch costs $50,000-$500,000+, (3) trademark infringement lawsuits are expensive ($100,000-$500,000), and (4) early detection of conflicts allows modifications before investing in brand development. A thorough clearance search protects your investment and ensures long-term brand viability."
    },
    {
      question: "What's the difference between a USPTO search and a comprehensive clearance search?",
      answer: "USPTO TESS database search only covers federal registered and pending trademarks—about 30% of potential conflicts. Comprehensive clearance also searches: (1) state trademark registrations (secretary of state databases), (2) common law uses (businesses using marks without federal registration), (3) domain name registrations, (4) social media handles, (5) business directories, and (6) web presence. Common law marks have priority based on first use, even without registration. A basic USPTO search misses 70% of potential conflicts, making comprehensive clearance essential for true risk assessment."
    },
    {
      question: "When should I conduct a trademark clearance search?",
      answer: "Conduct clearance searches before: (1) filing a trademark application, (2) launching new brands or products, (3) making significant marketing investments, (4) registering domain names, (5) ordering inventory or packaging, (6) expanding into new markets or product lines, or (7) considering business acquisitions. The earlier you search, the less expensive modifications become. Ideally, search during brand development before any public use or investment. If you discover conflicts after launch, rebranding costs exponentially more than searching first."
    },
    {
      question: "What if the clearance search finds potential conflicts?",
      answer: "If we find conflicts, the report provides: (1) assessment of conflict severity (blocking vs. manageable), (2) likelihood of USPTO rejection, (3) infringement risk evaluation, (4) recommendation to proceed, modify, or abandon, and (5) suggested modifications to avoid conflicts. Options include: modifying the mark slightly, choosing different goods/services classes, adding disclaimers, or selecting alternative marks. Many conflicts can be resolved through strategic adjustments. If conflicts are insurmountable, we help you select alternative marks before wasting filing fees."
    },
    {
      question: "How long does a trademark clearance search take?",
      answer: "Basic USPTO search: 3-5 business days. Comprehensive clearance (federal, state, common law): 5-10 business days. Premium clearance with international search: 10-15 business days. Rush processing available for urgent situations (2-3 business days additional fee). Timeline includes data collection, attorney analysis, and report preparation. We prioritize thoroughness over speed—missing conflicts costs far more than a few extra days of searching."
    },
    {
      question: "Can I do a trademark search myself for free?",
      answer: "Yes, USPTO's TESS database is free and public. However, DIY searches have significant limitations: (1) complex search syntax requiring expertise, (2) interpretation requires legal training, (3) misses state and common law uses, (4) no analysis of likelihood of confusion, (5) failure to identify phonetic equivalents or design similarities, and (6) no strategic recommendations. DIY searches often miss critical conflicts or overestimate risk. Professional searches save money by: avoiding unnecessary application fees, preventing costly rebranding, and providing attorney analysis for informed decisions."
    },
    {
      question: "What does a trademark clearance report include?",
      answer: "Comprehensive clearance reports include: (1) executive summary of clearance recommendation, (2) complete search results with identical and similar marks, (3) analysis of each potential conflict, (4) likelihood of confusion assessment, (5) registration probability opinion, (6) common law use findings, (7) domain and social media availability, (8) strategic recommendations, (9) suggested modifications if conflicts exist, and (10) next steps guidance. The report provides documentation for business decisions and can be used to support registration or defend against infringement claims."
    },
    {
      question: "How much does trademark clearance cost?",
      answer: "Basic federal search: $299. Comprehensive clearance (federal, state, common law): $599. Premium clearance with international search: $999. Compare this to: USPTO filing fees ($250-$350 per class), rebranding costs ($50,000-$500,000+), or infringement litigation ($100,000-$500,000). Clearance represents 1-2% of total brand investment but prevents 90%+ of trademark problems. It's the most cost-effective trademark investment you'll make. Volume discounts available for multiple marks or portfolio clearance."
    },
    {
      question: "Does a clear search guarantee trademark registration approval?",
      answer: "No, clearance searches assess likelihood of approval but cannot guarantee it. USPTO examiners may find issues beyond trademark conflicts including: (1) descriptiveness, (2) geographic descriptiveness, (3) surname issues, (4) ornamental use, (5) likelihood of confusion with marks not yet indexed, or (6) specimens not showing trademark use. However, proper clearance eliminates the most common rejection reason (likelihood of confusion with prior marks), improving approval odds from 50% to 85-90%. Combined with attorney application preparation, success rates exceed 95%."
    },
    {
      question: "Should I register my domain name before conducting clearance?",
      answer: "No, conduct clearance before domain registration. If clearance reveals conflicts, you may need to select different marks and domains. Domain registrations ($10-$50/year) are relatively cheap, but changing domains after building web presence is expensive (lost SEO value, broken links, customer confusion). The proper sequence is: (1) clearance search, (2) domain registration, (3) trademark application, (4) brand development. This ensures your entire digital presence is protected and legally defensible. We check domain availability as part of comprehensive clearance."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Trademark Clearance Search Services - Avoid Registration Conflicts"
        description="Comprehensive trademark clearance searches to identify conflicts before filing. Attorney analysis included."
        keywords="trademark clearance, trademark search, trademark availability, brand clearance, trademark conflicts"
      />

      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=900w=1600&q=80q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF035B]/20 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Search className="w-4 h-4" />
                <span>Trademark Clearance Services</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Professional Trademark Clearance Search
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                Comprehensive trademark searches to identify conflicts before you invest in brand development. Federal, state, and common law searches with attorney risk analysis.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => openModal('registration')}
                  className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white px-8 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:scale-105"
                >
                  Start Clearance Search
                </button>
                <button 
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Speak with Attorney
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>5-10 Day Turnaround</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Attorney Analysis</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-lg font-bold mb-6">Why Search First?</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Avoid Wasted Filing Fees</strong>
                    <span className="text-gray-300">USPTO fees are non-refundable even if your application is rejected</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Prevent Costly Rebranding</strong>
                    <span className="text-gray-300">Changing brands after launch can cost $50,000-$500,000+</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Reduce Legal Risk</strong>
                    <span className="text-gray-300">Identify infringement risks before they become lawsuits</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Clearance Benefits
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Protect your brand investment with thorough trademark clearance
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="group bg-white hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 cursor-pointer hover:shadow-xl transition-all border border-gray-100">
                <benefit.icon className="w-12 h-12 text-[#FF035B] group-hover:text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 leading-relaxed group-hover:text-white/90 transition-colors duration-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Search Levels */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Multi-Level Search Coverage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive searches beyond basic USPTO database
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {searchLevels.map((level, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{level.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 mb-4">{level.description}</p>
                <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 px-4 py-2 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  {level.coverage}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Clearance Search Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From search to strategic recommendations
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] text-white rounded-xl p-6 shadow-lg">
                  <div className="text-4xl font-bold mb-2 opacity-50">{item.step}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-sm mb-4 text-white/90">{item.description}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock className="w-4 h-4" />
                    {item.timeline}
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-[#FF035B] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <ServicePricing showPrices={true} title="Clearance Search Packages" subtitle="Choose the plan that fits your needs" />


      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about trademark clearance
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
                >
                  <span className="font-semibold text-gray-900 pr-8">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[#FF035B] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Protect Your Brand Investment
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Don't risk costly rebranding or legal disputes. Conduct a comprehensive clearance search before investing in your brand.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={() => openModal('registration')}
              className="bg-white text-[#FF035B] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:shadow-xl"
            >
              Start Clearance Search
            </button>
            <button 
              onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF035B] transition-all"
            >
              Speak with Attorney
            </button>
          </div>
        </div>
      </section>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};

export default TrademarkClearancePage;
